import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { customToast } from "../../../../components/notify";
import { IApiVolume, IVolumeFormData } from "../../types";
import { volumeValidationSchema } from "../../validations/validations";
import { INITIAL_VOLUME_SETTINGS } from "../configs";
import { mergeKioskSettings } from "../utils/mergeKioskSettings";
import useKioskSettings from "./useKioskSettings";
import { useTabGuard } from "./useTabGuard";
import useUploadAssets from "./useUploadAssets";

interface UseVolumeSettingsProps {
    characterId: string;
}

// 預設音量設定（從 initial-values 取得）
const DEFAULT_VOLUME_FORM: IVolumeFormData = {
    bgm_file: null,
    bgm_url: INITIAL_VOLUME_SETTINGS.bgm_url,
    bgm_max: INITIAL_VOLUME_SETTINGS.bgm_max,
    bgm_min: INITIAL_VOLUME_SETTINGS.bgm_min,
    ad_video_max: INITIAL_VOLUME_SETTINGS.ad_video_max,
    ad_video_min: INITIAL_VOLUME_SETTINGS.ad_video_min,
    voice: INITIAL_VOLUME_SETTINGS.voice,
    effect: INITIAL_VOLUME_SETTINGS.effect,
};

export const useVolumeSettings = ({ characterId }: UseVolumeSettingsProps) => {
    const { t } = useTranslation();
    const { kioskSetting, isLoadingKioskSetting, isUpdatingKioskSetting, updateSettings } = useKioskSettings({
        characterId,
    });
    const { handleUploadFile, isUploading } = useUploadAssets();
    const [isUploadingBgm, setIsUploadingBgm] = useState(false);

    // ======================= 表單設置 =======================
    const methods = useForm<IVolumeFormData>({
        defaultValues: DEFAULT_VOLUME_FORM,
        resolver: yupResolver(volumeValidationSchema()),
        mode: "onChange",
    });

    const { control, handleSubmit, formState, reset, setValue } = methods;

    // Watched values
    const watchedBgmUrl = useWatch({ control, name: "bgm_url" });
    const watchedBgmFile = useWatch({ control, name: "bgm_file" });
    const watchedBgmMax = useWatch({ control, name: "bgm_max" });
    const watchedBgmMin = useWatch({ control, name: "bgm_min" });
    const watchedAdVideoMax = useWatch({ control, name: "ad_video_max" });
    const watchedAdVideoMin = useWatch({ control, name: "ad_video_min" });
    const watchedVoice = useWatch({ control, name: "voice" });
    const watchedEffect = useWatch({ control, name: "effect" });

    // ======================= 資料轉換 =======================
    const transformAPItoForm = useCallback((volumeData: IApiVolume | undefined): IVolumeFormData => {
        if (!volumeData) {
            return DEFAULT_VOLUME_FORM;
        }

        return {
            bgm_file: null,
            bgm_url: volumeData.bgm_url ?? "",
            bgm_max: volumeData.bgm_max ?? 0.8,
            bgm_min: volumeData.bgm_min ?? 0.5,
            ad_video_max: volumeData.ad_video_max ?? 0.8,
            ad_video_min: volumeData.ad_video_min ?? 0.5,
            voice: volumeData.voice ?? 1.0,
            effect: volumeData.effect ?? 0.6,
        };
    }, []);

    const transformFormToAPI = useCallback((formData: IVolumeFormData): IApiVolume => {
        return {
            bgm_url: formData.bgm_url,
            bgm_max: formData.bgm_max,
            bgm_min: formData.bgm_min,
            ad_video_max: formData.ad_video_max,
            ad_video_min: formData.ad_video_min,
            voice: formData.voice,
            effect: formData.effect,
        };
    }, []);

    // ======================= 檔案處理函數 =======================
    const handleBgmFileSelect = useCallback(
        (file: File | null) => {
            setValue("bgm_file", file, { shouldDirty: true });
        },
        [setValue]
    );

    const handleBgmDelete = useCallback(() => {
        setValue("bgm_file", null, { shouldDirty: true });
        setValue("bgm_url", "", { shouldDirty: true });
    }, [setValue]);

    // ======================= 提交處理函數 =======================
    const onSubmit = useCallback(
        async (data: IVolumeFormData) => {
            try {
                setIsUploadingBgm(true);

                // Upload BGM file if pending
                if (data.bgm_file) {
                    const response = await handleUploadFile({
                        file: data.bgm_file,
                        prefix: "audio",
                    });

                    if (response?.url) {
                        data.bgm_url = response.url;
                        data.bgm_file = null;
                        setValue("bgm_url", response.url);
                        setValue("bgm_file", null);
                    } else {
                        customToast.error(t("COMMON.UPLOAD_FAILED") || "上傳失敗");
                        setIsUploadingBgm(false);
                        return;
                    }
                }

                const apiPayload = transformFormToAPI(data);
                const currentNative = kioskSetting?.data?.native;
                const mergedNative = mergeKioskSettings(currentNative, { volume: apiPayload });

                await updateSettings({ native: mergedNative });

                // Reset form with new values to clear dirty state
                reset(data);
                customToast.success(t("COMMON.SETTINGS_SAVED") || "設定已儲存");
            } catch (error) {
                customToast.error(t("COMMON.SETTINGS_ERROR") || "儲存設定失敗");
            } finally {
                setIsUploadingBgm(false);
            }
        },
        [handleUploadFile, transformFormToAPI, kioskSetting, updateSettings, setValue, reset, t]
    );

    const saveHandler = useCallback(async () => {
        await handleSubmit(onSubmit)();
    }, [handleSubmit, onSubmit]);

    useTabGuard("volume", formState.isDirty, saveHandler);

    // ======================= 初始化表單從 API =======================
    useEffect(() => {
        // Only reset form when loading completes to avoid race conditions
        if (!isLoadingKioskSetting) {
            const volumeData = kioskSetting?.data?.native?.volume;
            const formData = transformAPItoForm(volumeData);
            reset(formData);
        }
    }, [kioskSetting?.data?.native?.volume, isLoadingKioskSetting, reset, transformAPItoForm]);

    return {
        // Form methods
        methods,
        handleSubmit,
        onSubmit,
        formState,

        // Watched values
        watchedBgmUrl,
        watchedBgmFile,
        watchedBgmMax,
        watchedBgmMin,
        watchedAdVideoMax,
        watchedAdVideoMin,
        watchedVoice,
        watchedEffect,

        // Loading states
        isLoadingKioskSetting,
        isUpdatingKioskSetting,
        isUploading,
        isUploadingBgm,

        // Handlers
        handleBgmFileSelect,
        handleBgmDelete,
    };
};

export default useVolumeSettings;
